import { useTrackEvent, useUrls } from 'common';
import { useMemo } from 'react';
import { APP_ROUTES } from '~/routes';
import {
  Button,
  NavMenu,
  NavMenuItemGroup,
  useBreakpoints,
} from '@xbto/universal-components';
import { DataStore } from '~/store';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from './use-authentication-hooks';
import { API } from '@xbto/api-client';

export const useFundsExperienceNav = (
  onPressAccountType: (type: API.AccountType) => void
) => {
  /**
   * Store
   */
  const hasAccountOfTypeCustody = DataStore.useStoreState(
    s => s.portfolio.hasAccountOfTypeCustody
  );
  const hasAccountOfTypeProTrading = DataStore.useStoreState(
    s => s.portfolio.hasAccountOfTypeProTrading
  );
  const hasAccountOfTypeTradingAndCash = DataStore.useStoreState(
    s => s.portfolio.hasAccountOfTypeTradingAndCash
  );
  const hasAccountOfTypeFund = DataStore.useStoreState(
    s => s.portfolio.hasAccountOfTypeFund
  );
  const username = DataStore.useStoreState(s => s.user.identity?.username);
  const globalAppSettings = DataStore.useStoreState(
    s => s.settings.globalAppSettings
  );
  const accountDetail = DataStore.useStoreState(s => s.portfolio.accountDetail);

  const setAccountDetail = DataStore.useStoreActions(
    a => a.portfolio.setAccountDetail
  );

  /**
   * Vars
   */
  const isAccountOfTypeFund = !!accountDetail?.isAccountOfTypeFund;
  const isAccountOfTypeTradingAndCash =
    !!accountDetail?.isAccountOfTypeTradingAndCash;
  const isAccountOfTypeVault = !!accountDetail?.isAccountOfTypeVault;
  const relationshipManager = DataStore.useStoreState(
    s => s.settings.relationshipManager
  );

  /**
   * Hooks
   */
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const trackEvent = useTrackEvent();
  const { breakpoints } = useBreakpoints();
  const { proTradingUrl } = useUrls();

  const activeTrader = useMemo(() => {
    return (
      hasAccountOfTypeProTrading &&
      proTradingUrl && (
        <Button
          variant="secondary"
          label="Active trader"
          size="sm"
          icon="OpenInNew"
          onPress={() => window.open(proTradingUrl, '_blank')}
        />
      )
    );
  }, [hasAccountOfTypeProTrading, proTradingUrl]);

  /**
   * DOM
   */
  const ROUTE_PRODUCT_FUND =
    APP_ROUTES.AUTH_PORTFOLIO_PRODUCT_SPLASH + `?type=${API.AccountType.Fund}`;
  const ROUTE_PRODUCT_PRO_TRADING =
    APP_ROUTES.AUTH_PORTFOLIO_PRODUCT_SPLASH +
    `?type=${API.AccountType.ProTrading}`;
  const ROUTE_PRODUCT_CUSTODY =
    APP_ROUTES.AUTH_PORTFOLIO_PRODUCT_SPLASH +
    `?type=${API.AccountType.Custody}`;

  const isMissingProductFundRoute = location.href.includes(ROUTE_PRODUCT_FUND);
  const isMissingProductTradingRoute = location.href.includes(
    ROUTE_PRODUCT_PRO_TRADING
  );
  const isMissingProductCustodyRoute = location.href.includes(
    ROUTE_PRODUCT_CUSTODY
  );

  // @ts-ignore import { IconName } from '@xbto/iconography';
  const menuItems: NavMenuItemGroup[] = useMemo(() => {
    const marketsPath = `${APP_ROUTES.AUTH_MARKETS}?assetType=Crypto`;
    return [
      {
        items: [
          {
            id: 'home',
            title: 'Home',
            icon: 'Home',
            active: pathname === APP_ROUTES.AUTH_PORTFOLIO_OVERVIEW,
            to: APP_ROUTES.AUTH_PORTFOLIO_OVERVIEW,
          },
          {
            id: 'markets',
            title: 'Markets',
            icon: 'BarChart',
            active: pathname.includes(APP_ROUTES.AUTH_MARKETS),
            to: marketsPath,
            onPress: e => {
              if (pathname === APP_ROUTES.AUTH_MARKETS) {
                e.preventDefault();
                navigate(marketsPath, { replace: true });
              }
            },
          },
        ],
      },
      {
        title: 'Products',
        items: [
          {
            id: 'funds',
            title: 'Funds',
            icon: 'Work',
            active:
              (pathname.includes(APP_ROUTES.AUTH_PORTFOLIO) &&
                isAccountOfTypeFund) ||
              isMissingProductFundRoute,
            onPress: () => {
              trackEvent('fund_nav-bar-item_clicked');

              if (!hasAccountOfTypeFund) {
                setAccountDetail(null);
                navigate(ROUTE_PRODUCT_FUND);
                return;
              }

              onPressAccountType(API.AccountType.Fund);
            },
          },
          {
            id: 'trading',
            title: 'Trading',
            icon: 'SyncAlt',
            active:
              (pathname.includes(APP_ROUTES.AUTH_PORTFOLIO) &&
                isAccountOfTypeTradingAndCash) ||
              isMissingProductTradingRoute,
            onPress: () => {
              trackEvent('trading_nav-bar-item_clicked');

              if (!hasAccountOfTypeTradingAndCash) {
                setAccountDetail(null);
                navigate(ROUTE_PRODUCT_PRO_TRADING);
                return;
              }

              onPressAccountType(API.AccountType.ProTrading);
            },
          },
          {
            id: 'custody',
            title: 'Custody',
            icon: 'Shield',
            active:
              (pathname.includes(APP_ROUTES.AUTH_PORTFOLIO) &&
                isAccountOfTypeVault) ||
              isMissingProductCustodyRoute,
            onPress: () => {
              trackEvent('custody_nav-bar-item_clicked');

              if (!hasAccountOfTypeCustody) {
                setAccountDetail(null);
                navigate(ROUTE_PRODUCT_CUSTODY);
                return;
              }

              onPressAccountType(API.AccountType.Custody);
            },
          },
        ],
      },
      {
        title: 'Account',
        items: [
          ...(hasAccountOfTypeCustody ||
          hasAccountOfTypeProTrading ||
          hasAccountOfTypeFund
            ? [
                {
                  id: 'statements',
                  title: 'Statements',
                  icon: 'Article',
                  active: pathname.includes(APP_ROUTES.AUTH_STATEMENTS),
                  to: APP_ROUTES.AUTH_STATEMENTS,
                },
              ]
            : []),
          {
            id: 'settings',
            title: 'Settings',
            icon: 'Settings',
            show: true,
            active: pathname.includes(APP_ROUTES.AUTH_SETTINGS),
            to: APP_ROUTES.AUTH_SETTINGS,
          },
          {
            id: 'sign-out',
            title: 'Log out',
            icon: 'Logout',
            show: true,
            onPress: () => logout(),
          },
        ],
      },
    ];
  }, [
    navigate,
    pathname,
    hasAccountOfTypeCustody,
    hasAccountOfTypeProTrading,
    hasAccountOfTypeTradingAndCash,
    hasAccountOfTypeFund,
    onPressAccountType,
    isAccountOfTypeFund,
    isAccountOfTypeTradingAndCash,
    isAccountOfTypeVault,
    isMissingProductFundRoute,
    isMissingProductTradingRoute,
    isMissingProductCustodyRoute,
    ROUTE_PRODUCT_FUND,
    ROUTE_PRODUCT_PRO_TRADING,
    ROUTE_PRODUCT_CUSTODY,
    setAccountDetail,
    trackEvent,
    logout,
  ]);

  return useMemo(() => {
    return {
      Nav: (
        <NavMenu
          floating={breakpoints.LESS_THAN_M}
          pathname={pathname}
          // @ts-ignore
          menuItems={menuItems}
          ActiveTrader={activeTrader}
          Link={Link}
          relationshipManager={relationshipManager}
          username={username}
          company={globalAppSettings?.organizationName || ''}
        />
      ),
    };
  }, [
    menuItems,
    breakpoints.LESS_THAN_M,
    activeTrader,
    relationshipManager,
    pathname,
    username,
    globalAppSettings?.organizationName,
  ]);
};
